import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Search,
  AlertTriangle,
  TrendingUp,
  Database,
  FileText,
  LogOut,
} from 'lucide-react';
import { User as UserType } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import kodiakLogo from '../../assets/images/kodiac-dark-640w.png';

interface LayoutProps {
  user: UserType;
  children: ReactNode;
}

export function Layout({ user, children }: LayoutProps) {
  const location = useLocation();
  const { signOut } = useAuth();

  // Determine current tool based on route
  const getCurrentTool = () => {
    const path = location.pathname;
    if (path.startsWith('/content-library')) return 'content';
    if (path.startsWith('/brand-agent')) return 'agent';
    return 'audit'; // Default to Audit (AI Discovery)
  };

  const currentTool = getCurrentTool();

  // Navigation structure based on active tool
  const getNavigationSections = () => {
    if (currentTool === 'content') {
      return [
        {
          title: 'OVERVIEW',
          items: [
            { name: 'Dashboard', href: '/content-library', icon: LayoutDashboard },
            { name: 'Connectors', href: '/content-library/connectors', icon: Database, badge: '9' },
          ],
        },
        {
          title: 'CONTENT',
          items: [
            { name: 'Records', href: '/content-library/records', icon: FileText, badge: '2.4K' },
            { name: 'AI readiness', href: '/content-library/ai-readiness', icon: TrendingUp },
            { name: 'Governance', href: '/content-library/governance', icon: AlertTriangle, badge: '14' },
          ],
        },
        {
          title: 'PUBLISH',
          items: [
            { name: 'Publish to AI', href: '/content-library/publish', icon: Search },
            { name: 'Sync log', href: '/content-library/sync-log', icon: FileText },
          ],
        },
      ];
    }

    if (currentTool === 'agent') {
      return [
        {
          title: 'OVERVIEW',
          items: [
            { name: 'Dashboard', href: '/brand-agent', icon: LayoutDashboard },
            { name: 'Configuration', href: '/brand-agent/config', icon: Database, badge: 'Live', badgeColor: 'green' },
          ],
        },
        {
          title: 'INTERACTIONS',
          items: [
            { name: 'Interaction log', href: '/brand-agent/interactions', icon: FileText, badge: '847' },
            { name: 'Sales intent', href: '/brand-agent/sales-intent', icon: TrendingUp },
            { name: 'Query monitor', href: '/brand-agent/query-monitor', icon: Search },
          ],
        },
        {
          title: 'CONFIGURATION',
          items: [
            { name: 'Guardrails', href: '/brand-agent/guardrails', icon: AlertTriangle },
            { name: 'MCP endpoints', href: '/brand-agent/mcp-endpoints', icon: Database },
            { name: 'Audit log', href: '/brand-agent/audit-log', icon: FileText },
          ],
        },
      ];
    }

    // Default: Audit tool
    return [
      {
        title: 'OVERVIEW',
        items: [
          { name: 'Dashboard', href: '/', icon: LayoutDashboard },
          { name: 'Visibility score', href: '/visibility-score', icon: TrendingUp },
        ],
      },
      {
        title: 'ANALYSIS',
        items: [
          { name: 'Inaccuracies', href: '/analysis', icon: AlertTriangle, badge: '7' },
          { name: 'Competitive', href: '/competitive', icon: TrendingUp },
        ],
      },
      {
        title: 'ACTIONS',
        items: [
          { name: 'Fix list', href: '/gaps', icon: Database, badge: '12', badgeColor: 'red' },
          { name: 'Reports', href: '/reports', icon: FileText },
        ],
      },
    ];
  };

  const navigationSections = getNavigationSections();

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-white flex">
      {/* Full-width Dark Sidebar - 230px */}
      <aside className="fixed left-0 top-0 bottom-0 w-[230px] bg-shell-navy flex flex-col z-50">
        {/* Logo */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <img src={kodiakLogo} alt="Kodiak" className="h-8" />
            <span className={`px-2 py-1 rounded text-xs font-semibold uppercase ${
              currentTool === 'audit' ? 'bg-t1-primary text-white' :
              currentTool === 'content' ? 'bg-t2-primary text-white' :
              'bg-t3-primary text-white'
            }`}>
              {currentTool === 'audit' ? 'Audit' : currentTool === 'content' ? 'Content' : 'Agent'}
            </span>
          </div>
        </div>

        {/* Navigation Sections */}
        <nav className="flex-1 overflow-y-auto py-4">
          {navigationSections.map((section) => (
            <div key={section.title} className="mb-6">
              <h3 className="px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                {section.title}
              </h3>
              {section.items.map((item: any) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center justify-between px-6 py-2.5 text-sm font-medium transition-colors ${
                      active
                        ? 'bg-t1-primary text-white'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5" />
                      <span>{item.name}</span>
                    </div>
                    {item.badge && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        item.badgeColor === 'red' ? 'bg-red-600 text-white' :
                        item.badgeColor === 'green' ? 'bg-green-600 text-white' :
                        'bg-blue-600 text-white'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Tool Switcher at Bottom */}
        <div className="border-t border-gray-700 p-4">
          <Link
            to="/"
            className={`flex items-center gap-3 px-3 py-2 mb-1 rounded transition-colors ${
              currentTool === 'audit'
                ? 'text-white'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <div className={`w-2 h-2 rounded-full ${currentTool === 'audit' ? 'bg-t1-primary' : 'bg-t1-primary opacity-50'}`} />
            <span className="text-sm font-medium">Kodiac Audit</span>
          </Link>
          <Link
            to="/content-library"
            className={`flex items-center gap-3 px-3 py-2 mb-1 rounded transition-colors ${
              currentTool === 'content'
                ? 'text-white'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <div className={`w-2 h-2 rounded-full ${currentTool === 'content' ? 'bg-t2-primary' : 'bg-t2-primary opacity-50'}`} />
            <span className="text-sm font-medium">Kodiac Content</span>
          </Link>
          <Link
            to="/brand-agent"
            className={`flex items-center gap-3 px-3 py-2 rounded transition-colors ${
              currentTool === 'agent'
                ? 'text-white'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <div className={`w-2 h-2 rounded-full ${currentTool === 'agent' ? 'bg-t3-primary' : 'bg-t3-primary opacity-50'}`} />
            <span className="text-sm font-medium">Kodiac Agent</span>
          </Link>
        </div>

        {/* User Section at Bottom */}
        <div className="border-t border-gray-700">
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-t1-primary flex items-center justify-center">
                <span className="text-sm font-semibold text-white">
                  {user.email.substring(0, 2).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user.email.split('@')[0]}</p>
                <p className="text-xs text-gray-400 truncate">Acme Corp - CMO</p>
              </div>
            </div>
            <button
              onClick={() => signOut()}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white rounded transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Content Area */}
      <main className="ml-[230px] flex-1 min-h-screen bg-white">
        {/* Page Content */}
        {children}
      </main>
    </div>
  );
}
